import { useEffect, useId, useRef, type ReactNode } from "react";

import * as css from "~/components/Dialog.css";

export interface DialogProps {
  title: ReactNode;
  hint?: ReactNode;
  size?: "sm" | "lg";
  onClose: () => void;
  children: ReactNode;
}

/**
 * A modal surface: a title, an optional hint, and whatever the caller renders
 * in it. It owns no form and no buttons; a form inside owns its own.
 *
 * Rendering it IS opening it, so showModal runs once, on mount; calling it
 * (rather than the `open` attribute) is what puts the element in the top
 * layer. Escape closes natively, and the caller unmounts it.
 */
export function Dialog({ title, hint, size = "sm", onClose, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className={css.dialog[size]}
      aria-labelledby={titleId}
      onCancel={onClose}
      onClose={onClose}
    >
      <div className={css.body}>
        <div>
          <h2 id={titleId} className={css.title}>
            {title}
          </h2>
          {hint !== undefined ? <span className={css.hint}>{hint}</span> : null}
        </div>
        {children}
      </div>
    </dialog>
  );
}
